import { isSodimacStandalone, isTottusStandalone } from '@digital-retail/store-manager';
import { cx } from 'cvb';
import PropTypes from 'prop-types';
import { useState } from 'react';
import AddToCartButton from '../../../../components/AddToCartButton/Desktop/AddToCartButtonDesktop';
import AddToCartButtonMobile from '../../../../components/AddToCartButton/Mobile/AddToCartButtonMobile';
import { AdditionalPDPLabel } from '../../../../components/AdditionalPDPLabel/AdditionalPDPLabel';
import { Badges } from '../../../../components/Badges/Badges';
import InstallmentsWithoutInterest from '../../../../components/InstallmentsWithoutInterest/InstallmentsWithoutInterest';
import InternationalShipping from '../../../../components/InternationalShipping/InternationalShipping';
import MultipurposeBadge from '../../../../components/MultipurposeBadge/MultipurposeBadge';
import { VariantId } from '../../../../components/PDP';
import ProductColorSwatches from '../../../../components/ProductColorSwatches/ProductColorSwatches';
import ProductPrices from '../../../../components/ProductPrices/ProductPrices';
import { ReaconditionedContainer } from '../../../../components/ReaconditionedContainer/ReaconditionedContainer';
import SellerInfo from '../../../../components/SellerInfo/SellerInfo';
import Size from '../../../../components/Size/Size';
import StockQuantity from '../../../../components/StockQuantity/StockQuantity';
import BodyCopy from '../../../../components/ui/BodyCopy/BodyCopy';
import ScrollTo from '../../../../components/ui/ScrollTo/ScrollTo';
import StockTable from '../../../../components/ui/StockTable/StockTable';
import constants from '../../../../config/constants';
import { withApplicationContext } from '../../../../utils/ApplicationContext';
import { withDigitalData } from '../../../../utils/DigitalDataContext';
import { withLabels } from '../../../../utils/LabelsContext';
import _ from '../../../../utils/LodashImports';
import { withProduct } from '../../../../utils/ProductContext';
import { noop } from '../../../../utils/noop';
import {
  getPriceForOutOfStock,
  getSizeSelectionLabel,
  isReaconditionedProduct,
} from '../../../../utils/product/productUtils';
import { getVariant } from '../../../../utils/variant';
import { SKUIdSeller } from '../../../common/PDPBasicInfoShared';
import { GetSharedDetails } from '../../shared';
import useInput from '../../useInput';
import { ProductSpecificationsMobileStyle } from './ProductSpecificationMobile.style';

const {
  MAX_CART_PRODUCT,
  FIRST_PARTY_SELLER_REGEX,
  PRODUCT_CONSTANTS,
  MODEL_CHARACTERISTICS_SPECIFICATION_ID,
  DEVICE_MOBILE,
} = constants;

const ProductSpecificationsMobile = ({
  product,
  labels,
  appCtx,
  isPumAvailable,
  pumQuantity,
  primeShipping,
  colorVariants,
  selectedColorSwatchIndex,
  colorSwatchHandler,
  sizes,
  selectedSize,
  sizeChangeHandler,
  handleAddToCart,
  isAddToCartInProgress,
  handleRemoveFromCart,
  isRemoveFromCartInProgress,
  addToCartButtonVisible,
  showCollectionButtonVisible,
  isVendorLogin,
  storeSkuData,
  currentTab,
  errorAddingToCart,
  requestProductFromStore,
  digitalData,
  setShowLoginForm,
  showLoginForm,
  setErrorAddingToCart,
  sizeChartData,
  fomo,
  variantSwatchType,
  sizeChartTable,
  brandLogoUrl,
}) => {
  const { regionCode, deviceType, store, siteConfig, comuna: comunaData, isDefaultComuna } = appCtx || {};
  const { toggles } = siteConfig;
  const isMobile = deviceType === DEVICE_MOBILE;
  const isSoCom = isSodimacStandalone(store);

  const {
    id: productId,
    currentVariant: currentVariantId,
    variants,
    additionalPDPLabels,
    isOutOfStock,
    colorMetrics,
    darSectionActive = false,
    warrantyOptions,
    serviceOptions,
    attributes,
    merchantCategoryId,
    isPublished,
  } = product;

  const {
    maxOnCartAmount = MAX_CART_PRODUCT,
    comboData = null,
    multipurposeBadges = [],
    prices,
    cmrPoints,
    discountBadge,
    badges = [],
    meatStickers = [],
    promotions,
    availability,
    offerings,
  } = getVariant(variants, currentVariantId);

  const {
    isMeatStickerApplicableInPDP = false,
    isStoreAvailabilityVisibleInPDP = false,
    isInternationalBuyVisiblePDP = false,
    isMultipurposeBadgeApplicable = false,
    isCMRCalculatorEnabledPDP = false,
  } = toggles;

  const {
    MODEL_CHARACTERISTICS_PREFIX,
    MODEL_CHARACTERISTICS_PREFIX_MOBILE,
    SELECTION_SIZE,
    COLOR,
    ALLOWED_3P_CAMPAIGN_NAMES_TO_RENDER,
  } = labels;

  const allowed3PCampaignNamesToRender = ALLOWED_3P_CAMPAIGN_NAMES_TO_RENDER || 'ENVIO GRATIS 3P, ENVIO_GRATIS_3P';

  const { pumUnits, specifications = [], isPrimeAvailable } = attributes;
  const sellerName = _.get(variants, '[0].offerings[0].sellerName', '');
  const is1pSeller = FIRST_PARTY_SELLER_REGEX.test(sellerName.toLowerCase());
  const { showQuantityError, inputVal, handleInputChange, setQuantityError, setInputVal } = useInput(maxOnCartAmount);

  const getModelsBodyCharacteristics = () => {
    const modelo = specifications.find((spec) => MODEL_CHARACTERISTICS_SPECIFICATION_ID.includes(spec.name));
    return modelo && `${MODEL_CHARACTERISTICS_PREFIX} ${modelo.value}`;
  };

  const modelCharacteristics = getModelsBodyCharacteristics();
  digitalData.pdp.setProductFeatures(modelCharacteristics ? '1' : '');
  const isStoreDataVaild = storeSkuData && Object.keys(storeSkuData).length > 0;

  const isReaconditionedProd = isReaconditionedProduct(product);
  const nonEventMeatStickers = (meatStickers || []).filter((sticker) => !['event'].includes(sticker.type));

  const freeShippingPromotionFor3p = promotions?.find((promotion) =>
    allowed3PCampaignNamesToRender
      .split(',')
      .map((name) => name.trim())
      .includes(promotion.campaignName)
  );

  const hasStickers = (isMeatStickerApplicableInPDP && meatStickers.length > 0) || freeShippingPromotionFor3p;

  const addToCartLabel = _.get(additionalPDPLabels, 'addToCart.enabled', false)
    ? _.get(additionalPDPLabels, 'addToCart', {})
    : null;

  const sizeSelectionLabel = getSizeSelectionLabel(product, SELECTION_SIZE);

  const selectedZone = comunaData?.data || null;

  const defaultZoneConfig =
    selectedZone && !_.isEmpty(selectedZone)
      ? selectedZone
      : {
          region: {},
          comuna: {},
        };

  const [zoneConfig, setZoneConfig] = useState(defaultZoneConfig);

  if (isOutOfStock) {
    return (
      <div
        className="product-specifications"
        data-backend-category={merchantCategoryId}
        data-availability={attributes && isPrimeAvailable}
        data-published={isPublished}
        data-variations={variants.length}
      >
        <div className="product-specifications-column">
          <div className={cx(!isMobile && 'flex')}>
            <div>
              {prices && prices.length > 0 && (
                <div className="prices">
                  <ProductPrices
                    isVendorLogin={isVendorLogin}
                    currentTab={currentTab}
                    storeSkuData={storeSkuData}
                    prices={getPriceForOutOfStock(prices)}
                    productId={productId}
                    regionCode={regionCode}
                    cmrPoints={cmrPoints}
                    isPumAvailable={isPumAvailable}
                    pumQuantity={pumQuantity}
                    pumUnit={pumUnits}
                    primeShipping={primeShipping}
                    deviceType={DEVICE_MOBILE}
                    isMobile={isMobile}
                  />
                </div>
              )}
              <SellerInfo product={product} />
              {isReaconditionedProd && <ReaconditionedContainer appCtx={appCtx} productAttributes={product} />}
            </div>
          </div>
        </div>
        <style jsx>{ProductSpecificationsMobileStyle}</style>
      </div>
    );
  }

  return (
    <div
      className="product-specifications"
      data-backend-category={merchantCategoryId}
      data-availability={attributes && isPrimeAvailable}
      data-published={isPublished}
      data-variations={variants.length}
    >
      <div className="product-specifications-column">
        <div className={cx(!isMobile && 'flex')}>
          <div>
            {!isSodimacStandalone(store) && !isTottusStandalone(store) && (
              <>
                <SellerInfo product={product} />
                {colorVariants[selectedColorSwatchIndex] && (
                  <div className="color-swatch-container fa--color-swatches__mobile">
                    <span>{`${COLOR}: `}</span>
                    {colorVariants[selectedColorSwatchIndex].label}
                    <ProductColorSwatches
                      colorVariants={colorVariants}
                      productId={productId}
                      selectedColorSwatchIndex={selectedColorSwatchIndex}
                      colorSwatchHandler={colorSwatchHandler}
                      type={PRODUCT_CONSTANTS.SOFTLINE}
                      size="large"
                      appCtx={appCtx}
                      colorMetrics={colorMetrics}
                      variantSwatchType={variantSwatchType}
                    />
                  </div>
                )}
                {sizes.length ? (
                  <Size
                    title={sizeSelectionLabel}
                    sizes={sizes}
                    onSizeChange={sizeChangeHandler}
                    selectedSize={selectedSize}
                    sizeChartData={sizeChartData}
                    fomo={fomo}
                    sizeChartTable={sizeChartTable}
                    brandLogoUrl={brandLogoUrl}
                  />
                ) : null}
              </>
            )}
            {prices && prices.length > 0 && (
              <div
                className={`prices
              ${isMultipurposeBadgeApplicable && multipurposeBadges.length > 0 ? 'withMultipurposeBadge' : ''}
              `}
              >
                <ProductPrices
                  isVendorLogin={isVendorLogin}
                  currentTab={currentTab}
                  storeSkuData={storeSkuData}
                  prices={prices}
                  productId={productId}
                  regionCode={regionCode}
                  cmrPoints={cmrPoints}
                  isPumAvailable={isPumAvailable}
                  pumQuantity={pumQuantity}
                  pumUnit={pumUnits}
                  primeShipping={primeShipping}
                  deviceType={DEVICE_MOBILE}
                  isMobile={isMobile}
                  discountBadge={discountBadge}
                  darSectionActive={darSectionActive}
                />
              </div>
            )}
            {isSoCom && <InstallmentsWithoutInterest appCtx={appCtx} product={product} regionCode={regionCode} />}
            {(badges?.length || meatStickers?.length || freeShippingPromotionFor3p?.badge) && (
              <div className="badges">
                <Badges
                  deviceType="mobile"
                  badgeItems={isMeatStickerApplicableInPDP ? nonEventMeatStickers : badges}
                  layout="PDP"
                  isSticker={hasStickers}
                  promotionBadge={freeShippingPromotionFor3p?.badge}
                />
              </div>
            )}
            {isMultipurposeBadgeApplicable && multipurposeBadges.length > 0 && (
              <div className="multipurpose-badge">
                <MultipurposeBadge badgeItems={multipurposeBadges} deviceType="mobile" inPdp />
              </div>
            )}
            <StockQuantity />
            {(isSodimacStandalone(store) || isTottusStandalone(store)) && <SellerInfo product={product} />}

            {isInternationalBuyVisiblePDP && <InternationalShipping product={product} />}
            {isReaconditionedProd && <ReaconditionedContainer appCtx={appCtx} productAttributes={product} />}
            {addToCartLabel && <AdditionalPDPLabel label={addToCartLabel} type="addToCart" productType="softline" />}

            {(isSodimacStandalone(store) || isTottusStandalone(store)) && colorVariants[selectedColorSwatchIndex] && (
              <>
                {' '}
                <div className="color-swatch-container fa--color-swatches__mobile">
                  <span>{`${COLOR}: `}</span>
                  {colorVariants[selectedColorSwatchIndex].label}
                  <ProductColorSwatches
                    colorVariants={colorVariants}
                    productId={productId}
                    selectedColorSwatchIndex={selectedColorSwatchIndex}
                    colorSwatchHandler={colorSwatchHandler}
                    type={PRODUCT_CONSTANTS.SOFTLINE}
                    size="large"
                    appCtx={appCtx}
                    colorMetrics={colorMetrics}
                  />
                </div>
                {sizes.length ? (
                  <Size
                    title={sizeSelectionLabel}
                    sizes={sizes}
                    onSizeChange={sizeChangeHandler}
                    selectedSize={selectedSize}
                    sizeChartData={sizeChartData}
                    fomo={fomo}
                    sizeChartTable={sizeChartTable}
                    brandLogoUrl={brandLogoUrl}
                  />
                ) : null}
              </>
            )}
          </div>
          <div className="variant-seller-ids">
            <VariantId variantId={currentVariantId} labels={labels} isSoCom={isSoCom} />
            {SKUIdSeller({
              appCtx,
              offerings,
              labels,
              regionCode,
              product,
            })}
          </div>
          {isVendorLogin && isStoreDataVaild && (
            <div className="stock-table">
              <StockTable
                stockData={storeSkuData}
                colorVariants={colorVariants}
                selectedColorSwatchIndex={selectedColorSwatchIndex}
                sizes={sizes}
                isMobile={isMobile}
                requestProductFromStore={requestProductFromStore}
                regionCode={regionCode}
              />
            </div>
          )}
        </div>
        {addToCartButtonVisible && isVendorLogin && (
          <AddToCartButton
            mobileView
            addToCartButtonVisible={addToCartButtonVisible}
            isAddToCartInProgress={isAddToCartInProgress}
            labels={labels}
            handleAddToCart={handleAddToCart}
            isVendorLogin={isVendorLogin}
            sizes={sizes}
            selectedSize={selectedSize}
            setShowLoginForm={setShowLoginForm}
            showLoginForm={showLoginForm}
            setErrorAddingToCart={setErrorAddingToCart}
            showQuantityError={showQuantityError}
            setQuantityError={setQuantityError}
            inputVal={inputVal}
            setInputVal={setInputVal}
            handleInputChange={handleInputChange}
          />
        )}
        {modelCharacteristics && (
          <div className="additional-info">
            <BodyCopy isHighlighted size="copy14">
              {MODEL_CHARACTERISTICS_PREFIX_MOBILE}
            </BodyCopy>
            <BodyCopy size="copy14">
              <p className="info-label">{modelCharacteristics}</p>
            </BodyCopy>
            <ScrollTo scrollToElementId="productInfoContainer" type="softline" isSoCom={isSoCom} />
          </div>
        )}

        <GetSharedDetails
          sizes={sizes}
          colorVariants={colorVariants}
          regionCode={regionCode}
          store={store}
          is1pSeller={is1pSeller}
          isStoreAvailabilityVisibleInPDP={isStoreAvailabilityVisibleInPDP}
          showCollectionButtonVisible={showCollectionButtonVisible}
          handleAddToCart={handleAddToCart}
          comboData={comboData}
          product={product}
          labels={labels}
          showCMRCalculator={isCMRCalculatorEnabledPDP}
          layout="softline"
          isSoCom={isSoCom}
          appCtx={appCtx}
          availability={availability}
          sellerName={sellerName}
          currentVariantId={currentVariantId}
          darSectionActive={darSectionActive}
          warrantyOptions={warrantyOptions}
          serviceOptions={serviceOptions}
          selectedZone={selectedZone}
          zoneConfig={zoneConfig}
          setZoneConfig={setZoneConfig}
          isDefaultComuna={isDefaultComuna}
        />

        {addToCartButtonVisible && !isVendorLogin && (
          <AddToCartButtonMobile
            maxCartQuantity={maxOnCartAmount}
            labels={labels}
            errorAddingToCart={errorAddingToCart}
            isAddToCartInProgress={isAddToCartInProgress}
            appCtx={appCtx}
            handleAddToCart={handleAddToCart}
            handleRemoveFromCart={handleRemoveFromCart}
            isRemoveFromCartInProgress={isRemoveFromCartInProgress}
            showQuantityError={showQuantityError}
            setQuantityError={setQuantityError}
            inputVal={inputVal}
            setInputVal={setInputVal}
            handleInputChange={handleInputChange}
            sizes={sizes}
            selectedSize={selectedSize}
          />
        )}
      </div>
      <style jsx>{ProductSpecificationsMobileStyle}</style>
    </div>
  );
};

ProductSpecificationsMobile.defaultProps = {
  isPumAvailable: false,
  pumQuantity: 0,
  primeShipping: false,
  colorVariants: [],
  selectedColorSwatchIndex: 0,
  colorSwatchHandler: noop,
  sizes: [],
  selectedSize: '',
  sizeChangeHandler: noop,
  handleAddToCart: noop,
  requestProductFromStore: noop,
  handleRemoveFromCart: noop,
  isRemoveFromCartInProgress: false,
  isAddToCartInProgress: false,
  addToCartButtonVisible: true,
  showCollectionButtonVisible: false,
  storeSkuData: {},
  currentTab: '',
  errorAddingToCart: false,
  showLoginForm: false,
  sizeChartData: {},
  fomo: {},
  variantSwatchType: undefined,
  sizeChartTable: null,
  brandLogoUrl: null,
};

ProductSpecificationsMobile.propTypes = {
  product: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  isPumAvailable: PropTypes.bool,
  pumQuantity: PropTypes.number,
  primeShipping: PropTypes.bool,
  colorVariants: PropTypes.array,
  selectedColorSwatchIndex: PropTypes.number,
  colorSwatchHandler: PropTypes.func,
  sizes: PropTypes.arrayOf(PropTypes.object),
  selectedSize: PropTypes.string,
  sizeChangeHandler: PropTypes.func,
  requestProductFromStore: PropTypes.func,
  handleAddToCart: PropTypes.func,
  isAddToCartInProgress: PropTypes.bool,
  handleRemoveFromCart: PropTypes.func,
  isRemoveFromCartInProgress: PropTypes.bool,
  addToCartButtonVisible: PropTypes.bool,
  showCollectionButtonVisible: PropTypes.bool,
  isVendorLogin: PropTypes.bool.isRequired,
  storeSkuData: PropTypes.object,
  currentTab: PropTypes.string,
  errorAddingToCart: PropTypes.bool,
  digitalData: PropTypes.object.isRequired,
  setShowLoginForm: PropTypes.func.isRequired,
  setErrorAddingToCart: PropTypes.func.isRequired,
  showLoginForm: PropTypes.bool,
  sizeChartData: PropTypes.object,
  fomo: PropTypes.object,
  variantSwatchType: PropTypes.string,
  sizeChartTable: PropTypes.object,
  brandLogoUrl: PropTypes.string,
};

export default withProduct(withLabels(withApplicationContext(withDigitalData(ProductSpecificationsMobile))));
